<template>
  <div class="model-split-action" :class="`model-split-action--${type}`">
    <div class="model-split-action__surface">
      <div class="model-split-action__row" :class="{ 'model-split-action__row--single': options.length <= 1 }">
        <el-button
          class="model-split-action__trigger"
          :type="type"
          :size="size"
          :disabled="disabled"
          :loading="loading"
          @click="emit('trigger')"
        >
          {{ actionLabel }}
        </el-button>

        <el-dropdown
          v-if="options.length > 1"
          class="model-split-action__dropdown"
          :disabled="disabled"
          trigger="click"
          @command="handleCommand"
        >
          <el-button
            class="model-split-action__toggle"
            :type="type === 'primary' ? 'default' : type"
            :size="size"
            :disabled="disabled"
            :loading="loading"
            aria-label="切换模型"
          >
            <el-icon><ArrowDown /></el-icon>
          </el-button>
          <template #dropdown>
            <el-dropdown-menu>
              <el-dropdown-item
                v-for="item in options"
                :key="item.id"
                :command="item.id"
                :class="{ 'model-split-action__menu-item--active': item.id === currentModelId }"
              >
                <div class="model-split-action__menu-item">
                  <strong>{{ displayName(item) }}</strong>
                  <div v-if="metaTags(item).length" class="model-split-action__menu-tags">
                    <span v-for="tag in metaTags(item)" :key="`${item.id}-${tag}`">{{ tag }}</span>
                  </div>
                  <small v-if="menuDescription(item)" class="model-split-action__menu-description">{{ menuDescription(item) }}</small>
                </div>
              </el-dropdown-item>
            </el-dropdown-menu>
          </template>
        </el-dropdown>
      </div>

      <div v-if="currentLabel" class="model-split-action__meta">
        <div class="model-split-action__meta-copy">
          <span class="model-split-action__meta-label">当前模型</span>
          <strong>{{ currentLabel }}</strong>
        </div>
        <div v-if="currentMetaTags.length" class="model-split-action__meta-tags">
          <span v-for="tag in currentMetaTags" :key="tag" class="model-split-action__current-chip">{{ tag }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { ArrowDown } from '@element-plus/icons-vue'

import type { ModelCatalogItem } from '@/api/config'

const props = withDefaults(defineProps<{
  actionLabel: string
  options: ModelCatalogItem[]
  modelValue?: string
  loading?: boolean
  disabled?: boolean
  type?: 'primary' | 'success' | 'warning' | 'info' | 'default'
  size?: 'large' | 'default' | 'small'
}>(), {
  modelValue: '',
  loading: false,
  disabled: false,
  type: 'primary',
  size: 'default',
})

const emit = defineEmits<{
  (event: 'update:modelValue', value: string): void
  (event: 'trigger'): void
}>()

const currentOption = computed(() => {
  const preferred = props.options.find((item) => item.id === props.modelValue)
  if (preferred) {
    return preferred
  }
  return props.options.find((item) => item.is_default) || props.options[0] || null
})

const currentLabel = computed(() => displayName(currentOption.value))
const currentModelId = computed(() => String(currentOption.value?.id || '').trim())
const currentMetaTags = computed(() => metaTags(currentOption.value))

function displayName(item?: ModelCatalogItem | null) {
  const label = String(item?.label || '').trim()
  const model = String(item?.model || '').trim()
  if (label && !isSystemDefaultLabel(label)) {
    return label
  }
  return humanizeModelName(model) || model || label
}

function isSystemDefaultLabel(label: string) {
  return /^默认.+模型$/.test(String(label || '').trim())
}

function metaTags(item?: ModelCatalogItem | null) {
  const provider = providerLabel(String(item?.provider || '').trim())
  const capability = String(item?.capability || '').trim()
  return [provider, capability].filter(Boolean)
}

function menuDescription(item?: ModelCatalogItem | null) {
  return String(item?.description || '').trim()
}

function providerLabel(provider: string) {
  if (provider === 'volcengine') return '火山引擎'
  if (provider === 'ali_tongyi') return '阿里通义'
  if (provider === 'fal') return 'fal'
  return provider
}

function humanizeModelName(model: string) {
  const normalized = String(model || '').trim()
  const map: Record<string, string> = {
    'doubao-seedream-5-0-260128': 'Seedream 5.0 Lite',
    'doubao-seedream-4-0-250828': 'Seedream 4.0',
    'doubao-seedance-1-0-pro-250528': 'Seedance 1.0 Pro',
    'doubao-seedance-1-0-pro-fast-251015': 'Seedance 1.0 Pro Fast',
    'doubao-seedance-2-0-260128音画同生': 'Seedance 2.0',
    'qwen-image-plus-2026-01-09': 'Qwen Image Plus',
    'qwen-image-edit-max': 'Qwen Image Edit Max',
    'qwen-image-2.0-pro-2026-03-03': 'Qwen Image 2.0 Pro',
    'qwen-image-2.0-2026-03-03': 'Qwen Image 2.0',
    'wan2.7-image-pro': 'WanX 2.7 Pro',
    'wan2.7-image': 'WanX 2.7',
    'wan2.7-t2v': 'Wan 2.7 T2V',
    'wan2.7-r2v': 'Wan 2.7 R2V',
    'wan2.7-i2v': 'Wan 2.7 I2V',
    'pixverse/pixverse-v5.6-it2v': 'PixVerse V5.6',
    'wan2.6-i2v-flash': 'Wan 2.6 I2V Flash',
    'wan2.6-r2v-flash': 'Wan 2.6 R2V Flash',
    'kling-3.0': 'Kling 3.0',
    'kling-v2': 'Kling V2',
    'fal-ai/flux-pro': 'Flux.1 Pro',
    'fal-ai/flux-dev': 'Flux.1 Dev',
  }
  return map[normalized] || ''
}

function handleCommand(command: string) {
  emit('update:modelValue', String(command || '').trim())
}
</script>

<style scoped>
.model-split-action {
  --split-accent: #2563eb;
  --split-accent-strong: #1d4ed8;
  --split-accent-soft: rgba(37, 99, 235, 0.12);
  --split-surface: rgba(255, 255, 255, 0.88);
  display: grid;
  width: 100%;
  min-width: 0;
}

.model-split-action__surface {
  display: grid;
  gap: 12px;
  padding: 12px;
  border-radius: 24px;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.96), rgba(248, 250, 252, 0.94));
  border: 1px solid rgba(226, 232, 240, 0.92);
  box-shadow: 0 16px 34px rgba(15, 23, 42, 0.06);
}

.model-split-action--default {
  --split-accent: #0f766e;
  --split-accent-strong: #115e59;
  --split-accent-soft: rgba(15, 118, 110, 0.12);
}

.model-split-action--success {
  --split-accent: #047857;
  --split-accent-strong: #065f46;
  --split-accent-soft: rgba(4, 120, 87, 0.12);
}

.model-split-action--warning {
  --split-accent: #c2410c;
  --split-accent-strong: #9a3412;
  --split-accent-soft: rgba(194, 65, 12, 0.12);
}

.model-split-action--info {
  --split-accent: #475569;
  --split-accent-strong: #334155;
  --split-accent-soft: rgba(71, 85, 105, 0.12);
}

.model-split-action__row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 52px;
  gap: 8px;
  align-items: stretch;
}

.model-split-action__row--single {
  grid-template-columns: minmax(0, 1fr);
}

.model-split-action__dropdown {
  width: 100%;
}

.model-split-action :deep(.el-dropdown),
.model-split-action__trigger,
.model-split-action__toggle {
  width: 100%;
}

.model-split-action__trigger,
.model-split-action__toggle {
  min-height: 54px;
  border-radius: 16px;
  border: 1px solid rgba(203, 213, 225, 0.9);
  transition: transform 0.22s ease, box-shadow 0.22s ease, border-color 0.22s ease;
}

.model-split-action__toggle {
  padding: 0;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(248, 250, 252, 0.94));
  box-shadow: 0 8px 16px rgba(15, 23, 42, 0.04);
}

.model-split-action__toggle :deep(.el-icon) {
  font-size: 20px;
  color: #475569;
}

.model-split-action__trigger:hover,
.model-split-action__toggle:hover {
  transform: translateY(-1px);
}

.model-split-action :deep(.el-button.model-split-action__trigger) {
  font-size: 15px;
  font-weight: 800;
  letter-spacing: 0.01em;
  box-shadow: 0 12px 22px rgba(37, 99, 235, 0.14);
}

.model-split-action :deep(.el-button--primary.model-split-action__trigger) {
  background: linear-gradient(180deg, #5c8dff 0%, #4277f7 52%, var(--split-accent) 100%);
  border-color: rgba(80, 118, 255, 0.42);
}

.model-split-action--default :deep(.el-button--default.model-split-action__trigger),
.model-split-action--default :deep(.el-button--default.model-split-action__toggle) {
  border-color: rgba(15, 118, 110, 0.16);
}

.model-split-action--default :deep(.el-button--default.model-split-action__trigger) {
  background: linear-gradient(180deg, #1db3a2 0%, #14b8a6 48%, #0f766e 100%);
  color: #f8fafc;
  border-color: rgba(20, 184, 166, 0.34);
  box-shadow: 0 14px 26px rgba(15, 118, 110, 0.16);
}

.model-split-action :deep(.el-button.is-disabled.model-split-action__trigger),
.model-split-action :deep(.el-button.is-disabled.model-split-action__toggle) {
  transform: none;
}

.model-split-action__meta {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  align-items: center;
  min-width: 0;
  padding-top: 2px;
  border-top: 1px solid rgba(226, 232, 240, 0.76);
}

.model-split-action__meta-copy {
  display: grid;
  gap: 2px;
  min-width: 0;
}

.model-split-action__meta-tags {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 8px;
  align-items: center;
}

.model-split-action__meta-label {
  color: #64748b;
  font-size: 11px;
  font-weight: 700;
}

.model-split-action__current-chip {
  display: inline-flex;
  align-items: center;
  min-height: 24px;
  padding: 0 9px;
  border-radius: 999px;
  background: rgba(241, 245, 249, 0.96);
  color: #334155;
  font-size: 11px;
  font-weight: 700;
  box-shadow: inset 0 0 0 1px rgba(203, 213, 225, 0.92);
}

.model-split-action__meta strong {
  color: #10243d;
  font-size: 15px;
  line-height: 1.3;
  letter-spacing: -0.02em;
}

.model-split-action__menu-item {
  display: grid;
  gap: 6px;
  min-width: 232px;
  max-width: 280px;
}

.model-split-action__menu-item strong {
  color: #1f2937;
  font-size: 15px;
  line-height: 1.35;
}

.model-split-action__menu-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.model-split-action__menu-tags span {
  display: inline-flex;
  align-items: center;
  min-height: 22px;
  padding: 0 8px;
  border-radius: 999px;
  background: rgba(241, 245, 249, 0.96);
  color: #475569;
  font-size: 11px;
  font-weight: 700;
}

.model-split-action__menu-description {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  color: #64748b;
  font-size: 12px;
  line-height: 1.55;
}

.model-split-action :deep(.model-split-action__menu-item--active) {
  background: var(--split-accent-soft);
}

@media (max-width: 768px) {
  .model-split-action__row {
    grid-template-columns: minmax(0, 1fr) 64px;
  }

  .model-split-action__trigger,
  .model-split-action__toggle {
    min-height: 56px;
    border-radius: 20px;
  }

  .model-split-action__surface {
    padding: 12px;
    border-radius: 20px;
  }

  .model-split-action__meta {
    align-items: flex-start;
    flex-direction: column;
  }

  .model-split-action__meta-tags {
    justify-content: flex-start;
  }
}
</style>